import { COUNTRY_SHORTS } from '~/constants'
import { BookingStageEnum } from '#shared/lib/enums'

import type { BookingExpandedDetails, Nullable } from '~/types'

/**
 * Returns a collection of basic booking details, advanced properties, and
 * helpers.
 *
 * @param booking The booking to get the details for.
 * @returns The booking details.
 */
export function getBookingDetails(booking?: Nullable<BookingExpandedDetails>) {
  // Base properties.
  const bookingAcceptedOn = booking?.AcceptedOn ?? ''
  const bookingActiveGuestMoneyRequest = booking?.ActiveGuestMoneyRequest ?? null
  const bookingAdditionalBookingCharges = booking?.AdditionalBookingCharges ?? []
  const bookingAdults = booking?.Adults ?? 0
  const bookingAllowedPaymentModes = booking?.AllowedPaymentModes ?? []
  const bookingAmountDue = booking?.AmountDue ?? 0
  const bookingAppliedOwnerCredit = booking?.AppliedOwnerCredit ?? 0
  const bookingAppliedRentalCredit = booking?.AppliedRentalCredit ?? 0
  const bookingAvatar = booking?.Avatar ?? undefined
  const bookingBookingDrivers = booking?.BookingDrivers ?? []
  const bookingBookingPreferences = booking?.BookingPreferences ?? []
  const bookingCancellationFee = booking?.CancellationFee ?? 0
  const bookingCancellationPolicy = booking?.CancellationPolicy ?? ''
  const bookingCancellationType = booking?.CancellationType
  const bookingCancelledOn = booking?.CancelledOn ?? ''
  const bookingCanGuestCreateReview = booking?.CanGuestCreateReview ?? false
  const bookingCanGuestRequestMoney = booking?.CanGuestRequestMoney ?? false
  const bookingCanHostCreateReview = booking?.CanHostCreateReview ?? false
  const bookingCanHostRequestMoney = booking?.CanHostRequestMoney ?? false
  const bookingCanSendMoney = booking?.CanSendMoney ?? false
  const bookingChildren = booking?.Children ?? 0
  const bookingCountry = booking?.Country ? (COUNTRY_SHORTS[booking.Country as keyof typeof COUNTRY_SHORTS]) : undefined
  const bookingCustomTotalAddOnAmount = booking?.CustomTotalAddOnAmount ?? undefined
  const bookingCustomTotalRentalAmount = booking?.CustomTotalRentalAmount ?? undefined
  const bookingDaysInUS = booking?.DaysInUS ?? 0
  const bookingDeclineOrCancelReasonDetails = booking?.DeclineOrCancelReasonDetails
  const bookingDeliveryDistance = booking?.DeliveryDistance ?? 0
  const bookingDestination = booking?.Destination ?? ''
  const bookingDestinationLatitude = booking?.DestinationLatitude ?? 0
  const bookingDestinationLongitude = booking?.DestinationLongitude ?? 0
  const bookingDestinationPlaceId = booking?.DestinationPlaceId ?? ''
  const bookingDriverAdded = booking?.DriverAdded ?? false
  const bookingEndDate = booking?.EndDate ?? booking?.DateEnd ?? ''
  const bookingExpiredInstantBook = booking?.ExpiredInstantBook ?? false
  const bookingFirstPaymentDate = booking?.FirstPaymentDate ?? ''
  const bookingFullPaymentDate = booking?.FullPaymentDate ?? ''
  const bookingGuestRentalAgreementStatus = booking?.GuestRentalAgreementStatus ?? ''
  const bookingHasDelivery = booking?.HasDelivery ?? false
  const bookingHasDriver = booking?.HasDriver ?? false
  const bookingHasDriverApproved = booking?.HasDriverApproved ?? false
  const bookingHasPendingChangeRequest = booking?.HasPendingChangeRequest ?? false
  const bookingHasPendingDispute = booking?.HasPendingDispute ?? false
  const bookingHasPendingInsuranceClaim = booking?.HasPendingInsuranceClaim ?? false
  const bookingHasReviewableChangeRequest = booking?.HasReviewableChangeRequest ?? false
  const bookingHasRoadsideEvent = booking?.HasRoadsideEvent ?? false
  const bookingHasValidDriver = booking?.HasValidDriver ?? false
  const bookingHostRentalAgreementStatus = booking?.HostRentalAgreementStatus ?? ''
  const bookingId = booking?.Id ?? 0
  const bookingInspections = booking?.Inspections ?? []
  const bookingInsuranceClaim = booking?.InsuranceClaim ?? null
  const bookingInsurancePricingStrategy = booking?.InsurancePricingStrategy ?? ''
  const bookingInsuranceProtectionLevel = booking?.InsuranceProtectionLevel ?? null
  const bookingIsDuplicate = booking?.IsDuplicate ?? false
  const bookingIsFreshInstantBooking = booking?.IsFreshInstantBooking ?? false
  const bookingIsIncomeProtected = booking?.IsIncomeProtected ?? null
  const bookingIsInstabook = booking?.IsInstabook ?? false
  const bookingIsInsured = booking?.IsInsured ?? false
  const bookingIsPaused = booking?.IsPaused ?? false
  const bookingIsSeasonallyPriced = booking?.IsSeasonallyPriced ?? false
  const bookingIsShortStay = booking?.IsShortStay ?? false
  const bookingIsSpecialOffer = booking?.IsSpecialOffer ?? false
  const bookingLastFailedPaymentReason = booking?.LastFailedPaymentReason ?? ''
  const bookingDepositOptions = booking?.DepositOptions ?? null
  const bookingModifiedByChangeRequestOrByAdminAdditionalCharge = booking?.ModifiedByChangeRequestOrByAdminAdditionalCharge ?? false
  const bookingNoPaymentMethod = booking?.NoPaymentMethod ?? false
  const bookingNumberOfDays = booking?.NumberOfDays ?? 0
  const bookingNumberOfNights = booking?.NumberOfNight ?? 0
  const bookingOwnerCanCreateClaim = booking?.OwnerCanCreateClaim ?? false
  const bookingOwnerCanSubmitInsuranceClaim = booking?.OwnerCanSubmitInsuranceClaim ?? false
  const bookingOwnerCloseoutOn = booking?.OwnerCloseoutOn ?? null
  const bookingOwnerId = booking?.OwnerId ?? 0
  const bookingOwnerPayout = booking?.OwnerPayout ?? 0
  const bookingOwnerServiceFeePct = booking?.OwnerServiceFeePct ?? 0
  const bookingPaymentFailed = booking?.PaymentFailed ?? false
  const bookingPaymentMethod = booking?.PaymentMethod ?? null
  const bookingPaymentSchedule = booking?.PaymentSchedule ?? null
  const bookingPaymentSchedules = booking?.PaymentSchedules ?? []
  const bookingPets = Boolean(booking?.Pets)
  const bookingPreviousStage = booking?.PreviousStage ?? ''
  const bookingPriceBreakdown = booking?.PriceBreakdown ?? []
  const bookingPriceBreakdownSummary = booking?.PriceBreakdownSummary ?? []
  const bookingReferralCreditPayout = booking?.ReferralCreditPayout ?? 0
  const bookingReferralCreditRental = booking?.ReferralCreditRental ?? 0
  const bookingRenterCloseoutOn = booking?.RenterCloseoutOn ?? null
  const bookingRenterId = booking?.RenterId ?? 0
  const bookingRenterServiceFee = booking?.RenterServiceFee ?? 0
  const bookingRequestDate = booking?.RequestDate ?? ''
  const bookingRequestedPaymentMode = booking?.RequestedPaymentMode
  const bookingResidenceTaxPct = booking?.ResidenceTaxPct ?? 0
  const bookingReviewCanBeMadeOn = booking?.ReviewCanBeMadeOn ?? ''
  const bookingReviewCanNoLongerBeMadeOn = booking?.ReviewCanNoLongerBeMadeOn ?? ''
  const bookingReviews = booking?.Reviews ?? []
  const bookingRoadsideAssistanceDailyFee = booking?.RoadsideAssistanceDailyFee ?? 0
  const bookingRoadsideAssistanceState = booking?.RoadsideAssistanceState ?? false
  const bookingRoadsideOptions = booking?.RoadsideOptions ?? []
  const bookingRVId = booking?.RVId ?? ''
  const bookingRVName = booking?.RVName ?? ''
  const bookingRVSnapshot = booking?.RVSnapshot ?? null
  const bookingRvUsage = booking?.RvUsage
  const bookingScheduleBreakdown = booking?.ScheduleBreakdown ?? []
  const bookingSelectedDepositOption = booking?.SelectedDepositOption ?? null
  const bookingShortIntroduce = booking?.ShortIntroduce ?? ''
  const bookingStage = booking?.Stage
  const bookingStartDate = booking?.StartDate ?? booking?.DateStart ?? ''
  const bookingState = booking?.Stage ? BookingStageEnum[booking.Stage] : undefined
  const bookingStatus = booking?.Status ?? ''
  const bookingSubTotal = booking?.SubTotal ?? 0
  const bookingTaxRemittanceStatus = booking?.TaxRemittanceStatus ?? ''
  const bookingTripIsInProgress = booking?.TripIsInProgress ?? false
  const bookingType = booking?.Type
  const bookingUpsells = booking?.Upsells ?? []

  // Advanced properties.
  const bookingAdditionalChargesCompleted = bookingAdditionalBookingCharges.filter((charge) => 'Completed' === charge.Status)
  const bookingAdditionalChargesCurrent = bookingAdditionalBookingCharges.find((charge) => 'Cancelled' !== charge.Status && 'Completed' !== charge.Status)
  const bookingAdditionalChargesCurrentTotal
    = (bookingAdditionalChargesCurrent?.GeneratorAmount ?? 0)
      + (bookingAdditionalChargesCurrent?.MileageAmount ?? 0)
      + (bookingAdditionalChargesCurrent?.DamageAmount ?? 0)
      + (bookingAdditionalChargesCurrent?.OtherAmount ?? 0)
  const bookingAdditionalChargesDisputed = bookingAdditionalBookingCharges.filter((charge) => 'Disputed' === charge.Status)
  const bookingAdditionalChargesRequested = bookingAdditionalBookingCharges.some((charge) => 'Pending' === charge.Status || 'FailedPayment' === charge.Status)
  const bookingGuestDepartureInspection = findStartedInspection(bookingInspections, 'Renter', 'Departure')
  const bookingGuestReturnInspection = findStartedInspection(bookingInspections, 'Renter', 'Return')
  const bookingHostDepartureInspection = findStartedInspection(bookingInspections, 'Owner', 'Departure')
  const bookingHostReturnInspection = findStartedInspection(bookingInspections, 'Owner', 'Return')
  const bookingDepositsOptionLossDamageWaiver = booking?.DepositOptions?.find((option) => option.Type === 'LossDamageWaiver')
  const bookingDestinationCountry = bookingDestination.split(', ').pop()
  const bookingDestinationIsInCA = bookingDestination.endsWith('Canada')
  const bookingDestinationIsInUS = bookingDestination.endsWith('USA') || bookingDestination.endsWith('United States') || bookingDestination.endsWith('États-Unis')
  const bookingDistancePriceKms = bookingRVSnapshot?.IsChargeMileage ? (bookingRVSnapshot?.SurchargePerExtraKM ?? undefined) : undefined
  const bookingEndDateWithTimezone = bookingEndDate ? datetime.tz(bookingEndDate, bookingRVSnapshot?.Timezone ?? 'UTC') : ''
  const bookingOriginalRate = bookingRVSnapshot?.OriginalDefaultPrice ?? 0
  const bookingScheduleRemainder = bookingPaymentSchedules.find((schedule) => schedule.PaymentMode === 'RemainingBalance')
  const bookingStartDateWithTimezone = bookingStartDate ? datetime.tz(bookingStartDate, bookingRVSnapshot?.Timezone ?? 'UTC') : ''

  // Advanced `is` checks.
  const bookingIsCancelledWithPayoutGuarantee = Boolean(bookingIsIncomeProtected && bookingCancellationType === 'AdminCancellation')
  const bookingIsDelivery = Boolean(booking?.RvUsage === 'Delivery' || booking?.RvUsage === 'StationaryAfterDelivery')
  const bookingIsGoingToFestivalOrEvent = Boolean(bookingRVSnapshot?.RentalType !== 'RVCottage' && booking?.BookingPreferences?.some((preference) => preference.PreferenceType === 'AllowFestivalEventsParticipation' && preference.RequiredType === 'Required'))
  const bookingIsInCanada = bookingCountry === 'CA'
  const bookingIsInUs = bookingCountry === 'US'
  const bookingIsLossDamageWaiverUpgraded = Boolean(bookingDepositsOptionLossDamageWaiver?.Selected)
  const bookingIsPrepaid = bookingStage === 'AwaitingOwnerApproval' && !bookingNoPaymentMethod
  const bookingIsRoadsideUpgraded = Boolean(!bookingRoadsideOptions.find((option) => option.Type === 'Standard')?.IsCurrent)
  const bookingRentalContractIsSignedByOwner = bookingHostRentalAgreementStatus === 'Signed'
  const bookingRentalContractIsSignedByRenter = bookingGuestRentalAgreementStatus === 'Signed'

  // Advanced `has` checks.
  const bookingHasAllPaymentStepsCompleted = bookingScheduleBreakdown.length > 0 && bookingScheduleBreakdown.every((item) => item.Type === 'Completed')
  const bookingHasCustomQuote = Number(booking?.CustomTotalRentalAmount) > 0
  const bookingHasDispute = bookingAdditionalChargesDisputed.length > 0
  const bookingHasEnded = bookingStage ? ['Completed', 'CompleteInProcess', 'ProcessCompleteFail', 'PendingDispute', 'PendingInsuranceClaim'].includes(bookingStage) : false
  const bookingHasExperiencedDriver = Boolean(booking?.BookingPreferences?.find((preference) => preference.PreferenceType === 'AllowOnlyExperiencedSimilarRV' && preference.RequiredType !== 'NotRequired')?.RenterAnswer?.toLowerCase() === 'true')
  const bookingHasFomoTagsEnabled = !bookingHasCustomQuote
  const bookingHasInsuranceClaim = Boolean(bookingInsuranceClaim)
  const bookingHasMoneyRequested = ['Pending', 'FailedPayment'].includes(bookingAdditionalChargesCurrent?.Status ?? '')
  const bookingHasReviewPhotos = bookingReviews.some((review) => review?.Photos?.length)
  const bookingHasReviews = Boolean(bookingReviews.length)
  const bookingHasRoadsideUpsellOption = bookingRoadsideOptions.some((option) => option.IsUpsell)
  const bookingHasScheduleRemainder = bookingPaymentSchedules.some((schedule) => schedule.PaymentMode === 'RemainingBalance')
  const bookingHasVerifiedDrivers = bookingBookingDrivers.some((driver) => driver.Status === 'Approved')

  // Booking preferences helpers.
  const bookingPreferenceAttendingFestivalOrEvent = bookingBookingPreferences.find((preference) => preference.PreferenceType === 'AllowFestivalEventsParticipation')
  const bookingPreferenceFestivalOrEventDestination = bookingPreferenceAttendingFestivalOrEvent?.RenterAnswerDetails
  const bookingPreferenceSimilarRvExperience = bookingBookingPreferences.find((preference) => preference.PreferenceType === 'AllowOnlyExperiencedSimilarRV')

  // Stage helpers
  const bookingStageCanBeAccepted = bookingStage === 'AwaitingOwnerApproval'

  const bookingStageCanBeDeclined = bookingStage === 'EnquiryRequest'
    || bookingStage === 'InstantBooking'
    || bookingStage === 'Invoice'
    || bookingStage === 'AwaitingOwnerApproval'
    || bookingStage === 'DepositTaken'
    || bookingStage === 'SecurityDepositTaken'
    || bookingStage === 'FullPaymentReceived'
    || bookingStage === 'AwaitingPayment'

  const bookingStageCanEditDates = bookingStage === 'AwaitingOwnerApproval'
    || bookingStage === 'AwaitingPayment'
    || bookingStage === 'DepositTaken'
    || bookingStage === 'SecurityDepositTaken'
    || bookingStage === 'FullPaymentReceived'
    || bookingStage === 'TripStarted'

  const bookingStageCanEditDetails = bookingStage === 'AwaitingOwnerApproval'
    || bookingStage === 'AwaitingPayment'
    || bookingStage === 'InstantBooking'
    || bookingStage === 'DepositTaken'
    || bookingStage === 'SecurityDepositTaken'
    || bookingStage === 'FullPaymentReceived'

  const bookingStageIsAwaitingOwnerApproval = bookingStage === 'AwaitingOwnerApproval'
  const bookingStageIsAwaitingPayment = bookingStage === 'AwaitingPayment'

  const bookingStageIsCanceled = bookingStage === 'CanceledByTheAdmin'
    || bookingStage === 'CanceledByTheOwner'
    || bookingStage === 'CanceledByTheRenter'

  const bookingStageIsConfirmed = bookingStage === 'CompleteInProcess'
    || bookingStage === 'DepositTaken'
    || bookingStage === 'FullPaymentReceived'
    || bookingStage === 'InstantBooking'
    || bookingStage === 'PendingInsuranceClaim'
    || bookingStage === 'PendingPayout'
    || bookingStage === 'PendingPayoutOnCancel'
    || bookingStage === 'ProcessCompleteFail'
    || bookingStage === 'SecurityDepositTaken'
    || bookingStage === 'TripStarted'

  const bookingStageIsDeclined = bookingStage === 'DeclinedByTheOwner'
    || bookingStage === 'DeclinedByTheRenter'

  const bookingStageIsExpired = bookingStage === 'ExpiredByAdmin'
    || bookingStage === 'ExpiredInstantBook'
    || bookingStage === 'ExpiredNoPayment'
    || bookingStage === 'ExpiredRequest'

  const bookingStageIsInvoice = bookingStage === 'Invoice'

  const bookingStageHasStarted = bookingStage === 'TripStarted'
    || bookingStage === 'Completed'
    || bookingStage === 'CompleteInProcess'
    || bookingStage === 'Invoice'
    || bookingStage === 'ProcessCompleteFail'
    || bookingStage === 'PendingInsuranceClaim'

  const bookingStageInRoadsideAssistanceState = bookingStage === 'AwaitingOwnerApproval'
    || bookingStage === 'AwaitingPayment'
    || bookingStage === 'DepositTaken'
    || bookingStage === 'FullPaymentReceived'
    || bookingStage === 'SecurityDepositTaken'
    || bookingStage === 'Invoice'

  const bookingFinancial = booking?.Financial ?? null
  const bookingPromotionCode = booking?.PromotionCode ?? ''
  const bookingPromotionId = booking?.PromotionId ?? null

  return {
    bookingAcceptedOn,
    bookingActiveGuestMoneyRequest,
    bookingAdditionalBookingCharges,
    bookingAdults,
    bookingAllowedPaymentModes,
    bookingAmountDue,
    bookingAppliedOwnerCredit,
    bookingAppliedRentalCredit,
    bookingAvatar,
    bookingBookingDrivers,
    bookingBookingPreferences,
    bookingCancellationFee,
    bookingCancellationPolicy,
    bookingCancellationType,
    bookingCancelledOn,
    bookingCanGuestCreateReview,
    bookingCanGuestRequestMoney,
    bookingCanHostCreateReview,
    bookingCanHostRequestMoney,
    bookingCanSendMoney,
    bookingChildren,
    bookingCountry,
    bookingCustomTotalAddOnAmount,
    bookingCustomTotalRentalAmount,
    bookingDaysInUS,
    bookingDeclineOrCancelReasonDetails,
    bookingDeliveryDistance,
    bookingDestination,
    bookingDestinationLatitude,
    bookingDestinationLongitude,
    bookingDestinationPlaceId,
    bookingDriverAdded,
    bookingEndDate,
    bookingExpiredInstantBook,
    bookingFirstPaymentDate,
    bookingFullPaymentDate,
    bookingGuestDepartureInspection,
    bookingGuestRentalAgreementStatus,
    bookingGuestReturnInspection,
    bookingHasDelivery,
    bookingHasDriver,
    bookingHasDriverApproved,
    bookingHasPendingChangeRequest,
    bookingHasPendingDispute,
    bookingHasPendingInsuranceClaim,
    bookingHasReviewableChangeRequest,
    bookingHasRoadsideEvent,
    bookingHasValidDriver,
    bookingHostDepartureInspection,
    bookingHostRentalAgreementStatus,
    bookingHostReturnInspection,
    bookingId,
    bookingInsuranceClaim,
    bookingInsurancePricingStrategy,
    bookingInsuranceProtectionLevel,
    bookingIsDuplicate,
    bookingIsFreshInstantBooking,
    bookingIsIncomeProtected,
    bookingIsInstabook,
    bookingIsInsured,
    bookingIsPaused,
    bookingIsSeasonallyPriced,
    bookingIsShortStay,
    bookingIsSpecialOffer,
    bookingDepositOptions,
    bookingLastFailedPaymentReason,
    bookingModifiedByChangeRequestOrByAdminAdditionalCharge,
    bookingNoPaymentMethod,
    bookingNumberOfDays,
    bookingNumberOfNights,
    bookingOwnerCanCreateClaim,
    bookingOwnerCanSubmitInsuranceClaim,
    bookingOwnerCloseoutOn,
    bookingOwnerId,
    bookingOwnerPayout,
    bookingOwnerServiceFeePct,
    bookingPaymentFailed,
    bookingPaymentMethod,
    bookingPaymentSchedule,
    bookingPaymentSchedules,
    bookingPets,
    bookingPreviousStage,
    bookingPriceBreakdown,
    bookingPriceBreakdownSummary,
    bookingReferralCreditPayout,
    bookingReferralCreditRental,
    bookingRentalContractIsSignedByOwner,
    bookingRentalContractIsSignedByRenter,
    bookingRenterCloseoutOn,
    bookingRenterId,
    bookingRenterServiceFee,
    bookingRequestDate,
    bookingRequestedPaymentMode,
    bookingResidenceTaxPct,
    bookingReviewCanBeMadeOn,
    bookingReviewCanNoLongerBeMadeOn,
    bookingReviews,
    bookingRoadsideAssistanceDailyFee,
    bookingRoadsideAssistanceState,
    bookingRoadsideOptions,
    bookingRVId,
    bookingRVName,
    bookingRVSnapshot,
    bookingRvUsage,
    bookingScheduleBreakdown,
    bookingSelectedDepositOption,
    bookingShortIntroduce,
    bookingStage,
    bookingStartDate,
    bookingState,
    bookingStatus,
    bookingSubTotal,
    bookingTaxRemittanceStatus,
    bookingTripIsInProgress,
    bookingType,
    bookingUpsells,

    bookingAdditionalChargesCompleted,
    bookingAdditionalChargesCurrent,
    bookingAdditionalChargesCurrentTotal,
    bookingAdditionalChargesDisputed,
    bookingAdditionalChargesRequested,
    bookingDepositsOptionLossDamageWaiver,
    bookingDestinationCountry,
    bookingDestinationIsInCA,
    bookingDestinationIsInUS,
    bookingDistancePriceKms,
    bookingEndDateWithTimezone,
    bookingOriginalRate,
    bookingScheduleRemainder,
    bookingStartDateWithTimezone,

    bookingIsCancelledWithPayoutGuarantee,
    bookingIsDelivery,
    bookingIsGoingToFestivalOrEvent,
    bookingIsInCanada,
    bookingIsInUs,
    bookingIsLossDamageWaiverUpgraded,
    bookingIsPrepaid,
    bookingIsRoadsideUpgraded,

    bookingHasAllPaymentStepsCompleted,
    bookingHasCustomQuote,
    bookingHasDispute,
    bookingHasEnded,
    bookingHasExperiencedDriver,
    bookingHasFomoTagsEnabled,
    bookingHasInsuranceClaim,
    bookingHasMoneyRequested,
    bookingHasReviewPhotos,
    bookingHasReviews,
    bookingHasRoadsideUpsellOption,
    bookingHasScheduleRemainder,
    bookingHasVerifiedDrivers,

    bookingPreferenceAttendingFestivalOrEvent,
    bookingPreferenceFestivalOrEventDestination,
    bookingPreferenceSimilarRvExperience,

    bookingStageCanBeAccepted,
    bookingStageCanBeDeclined,
    bookingStageCanEditDates,
    bookingStageCanEditDetails,
    bookingStageHasStarted,
    bookingStageInRoadsideAssistanceState,
    bookingStageIsAwaitingOwnerApproval,
    bookingStageIsAwaitingPayment,
    bookingStageIsCanceled,
    bookingStageIsConfirmed,
    bookingStageIsDeclined,
    bookingStageIsExpired,
    bookingStageIsInvoice,

    bookingFinancial,
    bookingPromotionCode,
    bookingPromotionId,
  }
}
